﻿import { ReferenceFilterNode, ReferenceFilterOperator } from '@/shared/codeedit/dynamicFilterTypes.ts';
import { TypeGroup } from '@/shared/codeedit/dynamicFilterRules.ts';

/*
Renders a dynamic CodeRef filter predicate as readable text, one line per clause, so an existing
filter can be understood without opening DynamicFilterBuilderModal.vue.

Both reference fields and params are prefixed with library name and unquoted, and fixed text values are quoted.
 */

const operatorLabels: Record<ReferenceFilterOperator, string> = {
    And: 'AND',
    Or: 'OR',
    Eq: '=',
    Neq: '≠',
    Gt: '>',
    Lt: '<',
    Ge: '≥',
    Le: '≤',
    Exact: 'equals',
    Prefix: 'starts with',
    Suffix: 'ends with',
    Contains: 'contains',
    In: 'in'
};

function prefixed(library: string, name: string): string {
    return library ? `${library}.${name}` : name;
}

function quoteIfText(value: string, group?: TypeGroup): string {
    if (group === 'Numeric' || group === 'Bool' || group === 'Temporal') return value;
    return `'${value}'`;
}

function formatValue(node: ReferenceFilterNode, currentLibraryName: string): string {
    if (node.param) return prefixed(currentLibraryName, node.param);
    if (node.values && node.values.length > 0) {
        return `(${node.values.map(v => quoteIfText(v, node.group)).join(', ')})`;
    }
    if (node.value !== undefined && node.value !== null && node.value !== '') {
        return quoteIfText(node.value, node.group);
    }
    return '';
}

function formatClause(node: ReferenceFilterNode, referenceLibraryName: string, currentLibraryName: string, matchesCase: (node: ReferenceFilterNode) => boolean): string {
    const field = prefixed(referenceLibraryName, node.attribute ?? '');
    const op = operatorLabels[node.op];
    const value = formatValue(node, currentLibraryName);
    const caseSensitive = matchesCase(node) ? '(case-sensitive)' : '';
    return [field, op, value, caseSensitive].filter(part => part !== '').join(' ');
}

// One line per clause. From the second line on, the line starts with the group's AND/OR.
// matchesCase decides which rules are marked "(case-sensitive)". By default it reads the rule's
// caseSensitive flag, which only text rules have. To mark CodeRef rules too, pass ruleMatchesCase.
export function formatPredicateLines(
    node: ReferenceFilterNode | null,
    referenceLibraryName: string,
    currentLibraryName: string,
    matchesCase: (node: ReferenceFilterNode) => boolean = rule => rule.caseSensitive === true
): string[] {
    if (!node) return [];

    if (node.op === 'And' || node.op === 'Or') {
        const joiner = operatorLabels[node.op];
        return (node.clauses ?? []).flatMap((clause, idx) =>
            formatPredicateLines(clause, referenceLibraryName, currentLibraryName, matchesCase)
                .map((line, lineIdx) => idx > 0 && lineIdx === 0 ? `${joiner} ${line}` : line));
    }

    return [formatClause(node, referenceLibraryName, currentLibraryName, matchesCase)];
}
